import { Period } from '../../../../enums/reports';
import { useGetOccupancyQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import { Card, CardContent, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Occupancy } from '../../../../../app/Slices/SevenDayApiTypes';

interface OccupancyChartProps {
    period: Period;
    module?: number;
}

const OccupancyChart = ({ period, module }: OccupancyChartProps) => {
    const { data } = useGetOccupancyQuery({
        period,
        module,
    });

    const [transformedData, setTransformedData] = useState<Occupancy[]>();

    const theme = useTheme();

    useEffect(() => {
        if (!data) return;

        let transformedData = data[0].occupancy;

        if (period === Period.CurrentYear) {
            transformedData = data[0].occupancy.map((d) => ({
                ...d,
                periodStart: new Date(d.periodEnd).toLocaleString('default', {
                    month: 'long',
                }),
            }));
        }

        setTransformedData(transformedData);
    }, [data]);

    if (!data) return <></>;

    const getPeriod = (index: number) => {
        if (!transformedData || !transformedData[index]) return '';

        const { periodStart, periodEnd } = transformedData[index];

        const start = new Date(periodStart);
        const end = new Date(periodEnd);

        if (period === Period.CurrentYear) {
            return end.toLocaleString('default', {
                month: 'short',
            });
        }

        const formatOptions: Intl.DateTimeFormatOptions = {
            month: 'short',
            day: 'numeric',
        };

        const startStr = start.toLocaleDateString('en-US', formatOptions);
        const endStr = end.toLocaleDateString('en-US', formatOptions);

        if (startStr === endStr) return startStr;

        return `${startStr} - ${endStr}`;
    };

    return (
        <Card>
            <CardContent>
                <Typography variant='subHeading'>Occupancy</Typography>
                <Typography variant='subtitle2' gutterBottom>
                    {data[0].name}
                </Typography>
                <ResponsiveContainer width='100%' height={300}>
                    <BarChart data={transformedData}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis
                            dataKey='periodStart'
                            tickFormatter={(_, index) => getPeriod(index)}
                        />
                        <YAxis domain={[0, 100]} unit='%' />
                        <Tooltip
                            labelFormatter={(label) => {
                                const index = transformedData?.findIndex(
                                    (value) => value.periodStart === label,
                                );

                                if (!index || !transformedData) return label;

                                return (
                                    <>
                                        <text>{getPeriod(index)}</text>
                                        <br />
                                        <text>
                                            Available Nights:{' '}
                                            {
                                                transformedData[index]
                                                    ?.availableNights
                                            }
                                        </text>
                                        <br />
                                        <text>
                                            Occupied Nights:{' '}
                                            {
                                                transformedData[index]
                                                    ?.availableOccupiedNights
                                            }
                                        </text>
                                    </>
                                );
                            }}
                            formatter={(value: number) =>
                                `${value.toFixed(1)}%`
                            }
                        />
                        <Bar
                            dataKey='availableNightsOccupancy'
                            name='Occupancy'
                            fill={theme.palette.textAccent.main}
                        />
                    </BarChart>
                </ResponsiveContainer>
                <Typography variant='caption'>
                    **Occupancy calculated as the number of reserved nights,
                    divided by the number of available nights. Day reservations
                    and reservations that fall outside of current availability
                    are excluded from this number
                </Typography>
            </CardContent>
        </Card>
    );
};

export default OccupancyChart;
